import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "../../../components/AppToast";
import OrganizationHierarchy from "./OrganizationHierarchy";
import { INITIAL_ORG_FLAT, DEFAULT_MAX_ORG_LEVEL } from "./orgConstants";
import { migrateOrgFlat } from "./customerModel";
import {
  attachHierarchyMeta,
  flatRowToOrganizationUpdateRequest,
  normalizeOrganizationTreeResponse,
} from "./orgRemoteMappers";
import {
  fetchOrganizationTree,
  reparentOrganization,
  updateOrganization,
} from "../../../services/organization/organizationService";
import { isOrganizationApiEnabled } from "./orgApiConfig";
import "./organization-management.css";

const ORG_API_ENABLED = isOrganizationApiEnabled();

export default function OrganizationHierarchyManagement() {
  const userDetail = useSelector((state) => state?.login?.userDetail);
  const tenantId = userDetail?.tenantId;
  const [orgFlat, setOrgFlat] = useState(() => (ORG_API_ENABLED ? [] : migrateOrgFlat(INITIAL_ORG_FLAT)));

  const refreshTree = useCallback(async () => {
    if (!ORG_API_ENABLED || !tenantId) return;
    try {
      const raw = await fetchOrganizationTree(tenantId);
      const flat = normalizeOrganizationTreeResponse(raw);
      setOrgFlat(attachHierarchyMeta(flat));
    } catch (err) {
      const msg = typeof err === "string" ? err : err?.message || "Unknown error";
      toast.error(`Could not load organization tree: ${msg}`);
    }
  }, [tenantId]);

  useEffect(() => {
    if (ORG_API_ENABLED && tenantId) {
      refreshTree();
    }
  }, [tenantId, refreshTree]);

  const onPersistNodeToApi = useCallback(
    async (row, prevRow) => {
      if (!tenantId) return;
      if (prevRow && prevRow.parentId !== row.parentId) {
        await reparentOrganization(row.id, row.parentId ?? null);
      }
      await updateOrganization(row.id, flatRowToOrganizationUpdateRequest(row, { tenantId }));
    },
    [tenantId]
  );

  return (
    <div className="org-mgmt-page container-fluid px-2 px-md-3 py-3">
      <OrganizationHierarchy
        orgFlat={orgFlat}
        setOrgFlat={setOrgFlat}
        maxLevel={DEFAULT_MAX_ORG_LEVEL}
        onPersistNodeToApi={ORG_API_ENABLED && tenantId ? onPersistNodeToApi : undefined}
      />
    </div>
  );
}
